/**
 * Seam between a loyalty API and the local persisted store.
 *
 * - false (default, and the only working value today): the programme runs
 *   entirely off `useLoyaltyStore`, no network calls. Rules and rewards survive
 *   a reload because the store persists; nothing is shared between devices or
 *   staff members.
 * - true: `useLoyaltyQuery` would hydrate the store from the endpoint below and
 *   every mutation would mirror back. Components do not change between the two
 *   modes — they read the store and call the mutation hook, both of which honour
 *   this flag. Same arrangement as the floor planner and the roster.
 *
 * WHY IT IS OFF: `menu_core_be` has no loyalty domain at all. The pulled spec in
 * `src/api/core/api.json` carries 95 paths and not one of them mentions
 * loyalty, stamps, points or rewards, and `src/domain` has no such module. This
 * is not "the endpoint is not wired yet", it is "the endpoint does not exist".
 *
 * WHAT THE BACKEND WOULD NEED, roughly in the order it becomes useful:
 *
 * 1. The programme itself, one document per restaurant, addressed like the floor
 *    plan is: GET/PUT `/restaurants/{restaurantId}/loyalty`. Body is the whole
 *    document — rules plus the reward list — and PUT replaces it, so a reward
 *    absent from the payload is deleted. That is the shape this store already
 *    has, which is why the mapper below is a straight copy.
 *
 * 2. A guest identity that needs no registration. The idea this was designed
 *    around: the payment processor already hands back a stable token for the
 *    instrument used, so the token IS the account. That is a backend decision
 *    with real consequences and it should be checked against Viva's contract
 *    before anything is built on it — a token that rotates per transaction, or
 *    per device, silently turns every returning guest into a new one. Until
 *    someone confirms which of Viva's identifiers is stable across visits, this
 *    front end deliberately stores no guest data at all.
 *
 * 3. Balances and redemptions: POST a redemption from the waiter app, GET a
 *    guest's balance from the payment token. Those are what fill the four
 *    counters at the top of the page, which is why they read as "no data yet"
 *    rather than as zeros — a zero is a claim, and we have nothing to claim.
 *
 * 4. Somewhere to put a guest's e-mail or phone number, if the venue turns on
 *    the bonus for leaving one (`rules.contactBonusPoints`). That is the only
 *    part of this programme that touches personal data, so it needs more than a
 *    column: consent recorded with the contact, a retention period, and a way
 *    to delete on request. Until it exists the setting configures a prompt
 *    nothing can yet store — the number is saved, the contact is not.
 *
 * TWO THINGS THE DELETE CONTRACT HAS TO GET RIGHT, because this page promises
 * them — the confirmation on the bin says past redemptions stay in the reports:
 *
 * - Deleting a reward is a soft delete, and any report joining redemptions to
 *   rewards must NOT filter on `deleted_at IS NULL`, or every historical row
 *   for a deleted reward comes back with an empty name or breaks on a null.
 * - A redemption already `reserved` when the reward is deleted stays
 *   redeemable: the guest ordered before the owner pulled the reward, and the
 *   waiter app has to be able to hand it over.
 */
export const LOYALTY_USE_API = false

/** Backend path, relative to `/restaurants/{restaurantId}`. */
export const LOYALTY_ENDPOINT = '/loyalty'

/**
 * How long unused points live, counted from the guest's last visit rather than
 * from when they were earned — every visit renews the whole balance.
 *
 * Fixed, not a setting: it is a promise made to the guest at the till, and a
 * venue that shortens it retroactively expires points people believe they hold.
 * Version 1 of this page let it be typed in; nothing else in the product could
 * honour that number, which made it a field that only ever produced wrong copy.
 */
export const POINTS_VALIDITY_MONTHS = 12

/**
 * What a first reward tends to cost, in points — the example the hint under the
 * points rate is phrased around. A round number a manager can divide in their
 * head; the point of the line is the exchange rate, not this reward.
 */
export const TYPICAL_REWARD_POINTS = 200

/**
 * What a new reward's per-redemption limit starts at.
 *
 * One, because that is what almost every reward means: a free coffee is one
 * coffee. A new reward opens on this rather than on an empty required field, so
 * the common case is saved without typing anything into it.
 */
export const DEFAULT_MAX_PER_REDEMPTION = 1

/**
 * Bumped when the persisted shape changes.
 *
 * 2: `rules.pointsValidityDays` dropped.
 * 3: `rules.contactBonusPoints` added.
 * 4: `rewards[].maxPerRedemption` added.
 *
 * The store migrates rather than letting zustand discard the payload — a bumped
 * version with no `migrate` throws away `isActive`, the rules AND the whole
 * reward list, and rewards are typed in by hand with no backend to restore them
 * from.
 */
export const LOYALTY_SCHEMA_VERSION = 4
